// Groups Service - Academic group management for students
// Manages student group assignments by filière, niveau, and formation type

import { apiGet, apiPost, apiPatch, apiDelete } from '@/utils/api';
import type {
  GroupeWithCounts,
  StudentBasic,
  StudentCountByNiveau,
  AssignmentResult,
  CreateGroupeDto,
  UpdateGroupeDto,
  AssignStudentsDto,
  TypeFormation,
  GroupImpact,
} from '@/types/group.types';

// ==================== CAMPUS ENDPOINTS ====================

export interface CampusInfo {
  id: string;
  name: string;
  sigle: string | null;
  isMainCampus: boolean;
}

export interface CampusContextResponse {
  campuses: CampusInfo[];
  hasMultipleCampuses: boolean;
  userHasGlobalAccess: boolean;
}

/**
 * Get available campuses for group management
 * Returns campuses the user has access to and whether there are multiple campuses
 */
export async function getAvailableCampuses(): Promise<CampusContextResponse> {
  return apiGet<CampusContextResponse>('/groups/campuses');
}

// ==================== OVERVIEW ====================

export interface OverviewNiveau {
  niveau: number;
  fiCount: number;
  fcCount: number;
  fiAssigned: number;
  fcAssigned: number;
  fiPending: number;
  fcPending: number;
}

export interface OverviewFiliere {
  filiereId: string;
  codeFiliere: string;
  name: string;
  optionName: string | null;
  isOption: boolean;
  total: number;
  assigned: number;
  niveaux: OverviewNiveau[];
}

export interface OverviewCampus {
  campusId: string;
  campusName: string;
  total: number;
  filieres: OverviewFiliere[];
}

export interface GroupsOverview {
  academicYearId: string;
  grandTotal: number;
  uncategorized: number;
  campuses: OverviewCampus[];
}

/**
 * Whole-establishment student breakdown across campuses / filières / levels
 */
export async function getGroupsOverview(academicYearId?: string): Promise<GroupsOverview> {
  const params = new URLSearchParams();
  if (academicYearId) params.set('academicYearId', academicYearId);
  return apiGet<GroupsOverview>(`/groups/overview?${params.toString()}`);
}

// ==================== FILIÈRE NAVIGATION FOR GROUPS ====================

/**
 * Filiere data for group navigation (minimal subset)
 */
export interface FiliereForNavigation {
  id: string;
  codeFiliere: string;
  filiere: string;
  diplome: string;
  nombreAnnees: number;
  isActive: boolean;
  parentCodeFiliere: string | null;
  optionName: string | null;
  specialisationStartSemester?: number | null;
  options: {
    id: string;
    codeFiliere: string;
    filiere: string;
    diplome: string;
    nombreAnnees: number;
    optionName: string | null;
    isActive: boolean;
  }[];
}

/**
 * Get filieres for group navigation
 * This endpoint requires GROUP_VIEW permission (not FILIERE_VIEW)
 * Use this when users need to navigate to groups but don't have FILIERE_VIEW
 */
export async function getFilieresForGroupNavigation(activeOnly = true): Promise<FiliereForNavigation[]> {
  return apiGet<FiliereForNavigation[]>(`/groups/filieres-for-navigation?activeOnly=${activeOnly}`);
}

// ==================== GROUPE ENDPOINTS ====================

/**
 * Get all groups for a filière
 * Filters by campus if provided
 */
export async function getGroupesByFiliere(
  filiereId: string,
  academicYearId?: string,
  campusId?: string,
): Promise<GroupeWithCounts[]> {
  const params = new URLSearchParams();
  if (academicYearId) params.set('academicYearId', academicYearId);
  if (campusId) params.set('campusId', campusId);

  return apiGet<GroupeWithCounts[]>(`/groups/filieres/${filiereId}?${params.toString()}`);
}

/**
 * Get student counts by niveau for a filière
 * Filters by campus if provided
 */
export async function getStudentCountsByFiliere(
  filiereId: string,
  academicYearId: string,
  campusId?: string,
): Promise<StudentCountByNiveau[]> {
  const params = new URLSearchParams({ academicYearId });
  if (campusId) params.set('campusId', campusId);

  return apiGet<StudentCountByNiveau[]>(`/groups/filieres/${filiereId}/student-counts?${params.toString()}`);
}

/**
 * Create groups for a filière/niveau/type combination
 */
export async function createGroupes(
  filiereId: string,
  academicYearId: string,
  dto: CreateGroupeDto,
): Promise<GroupeWithCounts[]> {
  return apiPost<GroupeWithCounts[]>(`/groups/filieres/${filiereId}/groupes?academicYearId=${academicYearId}`, dto);
}

/**
 * Add more groups to an existing niveau/type combination
 */
export async function addGroupes(
  filiereId: string,
  academicYearId: string,
  niveau: number,
  typeFormation: TypeFormation,
  count: number,
  maxStudents?: number,
  campusId?: string,
): Promise<GroupeWithCounts[]> {
  return apiPost<GroupeWithCounts[]>(
    `/groups/filieres/${filiereId}/groupes/add?academicYearId=${academicYearId}`,
    { niveau, typeFormation, count, maxStudents, campusId }
  );
}

/**
 * Update a group
 */
export async function updateGroupe(
  groupeId: string,
  dto: UpdateGroupeDto,
): Promise<GroupeWithCounts> {
  return apiPatch<GroupeWithCounts>(`/groups/groupes/${groupeId}`, dto);
}

/**
 * What depends on a group (students, grades, attendance, schedule…) — shown
 * before a delete so the user knows what will be hidden (nothing is erased).
 */
export async function getGroupImpact(groupeId: string): Promise<GroupImpact> {
  return apiGet<GroupImpact>(`/groups/groupes/${groupeId}/impact`);
}

/**
 * Delete a group (soft delete: the group is hidden, its history is kept)
 */
export async function deleteGroupe(groupeId: string): Promise<void> {
  return apiDelete<void>(`/groups/groupes/${groupeId}`);
}

// ==================== STUDENT ASSIGNMENT ENDPOINTS ====================

/**
 * Get students with pending group assignment
 */
export async function getStudentsWithPendingAssignment(
  filiereId?: string,
  niveau?: number,
  typeFormation?: TypeFormation,
  academicYearId?: string,
  campusId?: string,
): Promise<StudentBasic[]> {
  const params = new URLSearchParams();
  if (filiereId) params.set('filiereId', filiereId);
  if (niveau) params.set('niveau', niveau.toString());
  if (typeFormation) params.set('typeFormation', typeFormation);
  if (academicYearId) params.set('academicYearId', academicYearId);
  if (campusId) params.set('campusId', campusId);

  return apiGet<StudentBasic[]>(`/groups/pending-students?${params.toString()}`);
}

/**
 * Get students in a specific group
 */
export async function getStudentsInGroup(groupeId: string): Promise<StudentBasic[]> {
  return apiGet<StudentBasic[]>(`/groups/groupes/${groupeId}/students`);
}

/**
 * Assign students to a group manually
 */
export async function assignStudentsToGroup(
  groupeId: string,
  dto: AssignStudentsDto,
): Promise<{ assignedCount: number }> {
  return apiPost<{ assignedCount: number }>(`/groups/groupes/${groupeId}/assign`, dto);
}

/**
 * Randomly assign pending students to groups
 */
export async function assignStudentsRandomly(
  filiereId: string,
  niveau: number,
  typeFormation: TypeFormation,
  academicYearId: string,
  campusId?: string,
): Promise<AssignmentResult> {
  const params = new URLSearchParams({ academicYearId });
  if (campusId) params.set('campusId', campusId);
  return apiPost<AssignmentResult>(
    `/groups/filieres/${filiereId}/niveau/${niveau}/assign-random?${params.toString()}`,
    { typeFormation }
  );
}

/**
 * Remove a student from a group. With `groupeId` only that link is closed;
 * without it, every link of the student's current academic year is closed.
 */
export async function removeStudentFromGroup(studentId: string, groupeId?: string): Promise<void> {
  const params = new URLSearchParams();
  if (groupeId) params.set('groupeId', groupeId);
  const query = params.toString();
  return apiDelete<void>(`/groups/students/${studentId}/group${query ? `?${query}` : ''}`);
}

/**
 * Reassign a student to a different group
 */
export async function reassignStudent(
  studentId: string,
  newGroupId: string,
): Promise<void> {
  return apiPatch<void>(`/groups/students/${studentId}/reassign`, { newGroupId });
}

/**
 * Change a student's formation type (FI/FC)
 */
export async function changeStudentTypeFormation(
  studentId: string,
  typeFormation: TypeFormation,
): Promise<void> {
  return apiPatch<void>(`/groups/students/${studentId}/type-formation`, { typeFormation });
}
